import { PlanStep, StepStatus, StepIntent, StepKind, PlanStatus, PlanRole, Participant } from '../types/generated/graphql';
import { colors } from '../theme/colors';

// ============================================================================
// Role Resolution Types & Functions
// ============================================================================

/**
 * Result of resolving a step's assignee through role-based assignment
 */
export interface ResolvedAssignment {
  /** The resolved participant, if any */
  participant: Participant | null;
  /** The role this step is assigned to, if any */
  role: PlanRole | null;
  /** How the assignment was resolved */
  source: 'override' | 'role' | 'none';
}

/**
 * Resolve the actual assignee for a step using role-based assignment.
 *
 * Resolution priority:
 * 1. If step.assignedToId exists → direct assignment (override)
 * 2. If step.assignedRoleId exists and role has defaultAssigneePersonId → role-based
 * 3. Otherwise → unassigned
 *
 * @param step The step to resolve assignment for
 * @param roles The plan's roles array
 * @param participants The plan's participants array
 * @returns The resolved assignment with participant, role, and source
 */
export function resolveStepAssignee(
  step: PlanStep,
  roles: PlanRole[],
  participants: Participant[]
): ResolvedAssignment {
  // Priority 1: Direct assignment (override)
  if (step.assignedToId) {
    const participant = participants.find(p => p.id === step.assignedToId) || null;
    const role = step.assignedRoleId
      ? roles.find(r => r.roleId === step.assignedRoleId) || null
      : null;
    return {
      participant,
      role,
      source: 'override',
    };
  }

  // Priority 2: Role-based assignment
  if (step.assignedRoleId) {
    const role = roles.find(r => r.roleId === step.assignedRoleId) || null;
    if (role?.defaultAssigneePersonId) {
      const participant = participants.find(p => p.id === role.defaultAssigneePersonId) || null;
      return {
        participant,
        role,
        source: 'role',
      };
    }
    // Role exists but no one is bound to it
    return {
      participant: null,
      role,
      source: 'role',
    };
  }

  // No assignment
  return {
    participant: null,
    role: null,
    source: 'none',
  };
}

/**
 * Resolve the current user's participant ID from the plan's participants list.
 * Matches by firebaseUid first, then falls back to email for unlinked participants only.
 *
 * Security: The email fallback only matches participants that have no firebaseUid set yet.
 * If a participant is already linked to a different Firebase account, it is never matched
 * by email, preventing impersonation edge cases.
 *
 * @param firebaseUid The current user's Firebase UID
 * @param email The current user's email (from Firebase Auth, not user input)
 * @param participants The plan's participants array
 * @returns The matching participant's ID, or null
 */
export function resolveCurrentParticipantId(
  firebaseUid: string | null,
  email: string | null | undefined,
  participants: Participant[]
): string | null {
  if (!firebaseUid) return null;
  // Primary: match by Firebase UID
  const byUid = participants.find((p) => p.firebaseUid === firebaseUid);
  if (byUid) return byUid.id;
  // Fallback: match by email, but ONLY for participants not yet linked to any Firebase account
  if (email) {
    const byEmail = participants.find(
      (p) => !p.firebaseUid && p.email?.toLowerCase() === email.toLowerCase()
    );
    return byEmail?.id || null;
  }
  return null;
}

/**
 * Get the display label for a resolved assignment
 * @param assignment The resolved assignment
 * @returns Display string like "Sarah" or "CPA (unassigned)" or "Unassigned"
 */
export function getAssignmentLabel(assignment: ResolvedAssignment): string {
  if (assignment.participant) {
    return assignment.participant.name;
  }
  if (assignment.role) {
    return `${assignment.role.name} (unassigned)`;
  }
  return 'Unassigned';
}

/**
 * Count steps assigned to each role
 * @param steps All steps in the plan
 * @param roles All roles in the plan
 * @returns Map of roleId to step count
 */
export function countStepsByRole(
  steps: PlanStep[],
  roles: PlanRole[]
): Map<string, number> {
  const counts = new Map<string, number>();

  // Initialize all roles with 0
  for (const role of roles) {
    counts.set(role.roleId, 0);
  }

  // Count steps
  for (const step of steps) {
    if (step.assignedRoleId && counts.has(step.assignedRoleId)) {
      counts.set(step.assignedRoleId, (counts.get(step.assignedRoleId) || 0) + 1);
    }
  }

  return counts;
}

/**
 * Get roles that are required but have no assignee
 * @param roles All roles in the plan
 * @returns Array of required unassigned roles
 */
export function getUnassignedRequiredRoles(roles: PlanRole[]): PlanRole[] {
  return roles.filter(r => r.required && !r.defaultAssigneePersonId);
}

// ============================================================================
// Status & Display Helpers
// ============================================================================

/**
 * Get styling for a step status pill
 */
export function getStatusPillStyle(status: StepStatus): {
  bgcolor: string;
  color: string;
  label: string;
} {
  switch (status) {
    case StepStatus.NotStarted:
      return {
        bgcolor: colors.gray[100],
        color: colors.gray[500],
        label: 'Not started',
      };
    case StepStatus.InProgress:
      return {
        bgcolor: colors.blue[50],
        color: colors.blue[600],
        label: 'In progress',
      };
    case StepStatus.Completed:
      return {
        bgcolor: `${colors.green[600]}15`,
        color: colors.green[600],
        label: 'Completed',
      };
    case StepStatus.Blocked:
      return {
        bgcolor: `${colors.red[600]}15`,
        color: colors.red[600],
        label: 'Blocked',
      };
    case StepStatus.WaitingExternal:
      return {
        bgcolor: `${colors.amber[600]}15`,
        color: colors.amber[600],
        label: 'Waiting for response',
      };
    default:
      return {
        bgcolor: colors.gray[100],
        color: colors.gray[500],
        label: status,
      };
  }
}

/**
 * Get color for a plan status
 */
export function getPlanStatusColor(status: PlanStatus): string {
  switch (status) {
    case PlanStatus.Active:
      return colors.green[600];
    case PlanStatus.Draft:
      return colors.gray[500];
    case PlanStatus.Completed:
      return colors.blue[600];
    case PlanStatus.Archived:
      return colors.amber[500];
    default:
      return colors.gray[500];
  }
}

/**
 * Get step type label and style based on intent
 */
export function getStepTypeInfo(
  step: PlanStep
): { label: string; bgcolor: string; color: string } | null {
  // Check intent first
  if (step.intent) {
    switch (step.intent) {
      case StepIntent.SendSignature:
        return {
          label: 'E-sign',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.SendNotification:
        // Check action endpoint to determine notification type
        const notificationAction = step.actions?.find(
          (a) =>
            a.endpoint?.includes('email') ||
            a.endpoint?.includes('sendgrid') ||
            a.endpoint?.includes('sms') ||
            a.endpoint?.includes('twilio')
        );
        if (
          notificationAction?.endpoint?.includes('email') ||
          notificationAction?.endpoint?.includes('sendgrid')
        ) {
          return {
            label: 'Email',
            bgcolor: colors.gray[100],
            color: colors.gray[500],
          };
        }
        if (
          notificationAction?.endpoint?.includes('sms') ||
          notificationAction?.endpoint?.includes('twilio')
        ) {
          return {
            label: 'SMS',
            bgcolor: colors.gray[100],
            color: colors.gray[500],
          };
        }
        return {
          label: 'Notification',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.DataEntry:
        return {
          label: 'Form / Drafting',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.ReviewApprove:
        return {
          label: 'Review',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Research:
        return {
          label: 'Research',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Custom:
        return {
          label: 'Custom',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.CollectDocument:
        return {
          label: 'Document',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Payment:
        return {
          label: 'Payment',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Schedule:
        return {
          label: 'Schedule',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Calculation:
        return {
          label: 'Calculation',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
      case StepIntent.Negotiation:
        return {
          label: 'Negotiation',
          bgcolor: colors.gray[100],
          color: colors.gray[500],
        };
    }
  }

  // Check if step has AI agent action
  const hasAgentAction = step.actions?.some((a) => a.mode === 'AGENT');
  if (hasAgentAction) {
    return {
      label: 'AI Agent',
      bgcolor: colors.gray[100],
      color: colors.gray[500],
    };
  }

  return null;
}

/**
 * Format a date string as relative time
 */
export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Check if all steps in a phase are completed
 */
export function isPhaseCompleted(phaseId: string, steps: PlanStep[]): boolean {
  const phaseSteps = steps.filter((s) => s.phaseId === phaseId);
  if (phaseSteps.length === 0) return false;
  return phaseSteps.every((s) => s.status === StepStatus.Completed);
}

// ============================================================================
// Automation Execution Helpers
// ============================================================================

/**
 * Check if a step can be executed (has automation capability).
 * Returns true for AUTOMATION and MIXED steps that have skills or actions attached.
 * 
 * @param step The step to check
 * @returns True if the step can be executed via automation
 */
export function canExecuteStep(step: PlanStep): boolean {
  // Only AUTOMATION and MIXED steps can be executed
  if (step.kind !== StepKind.Automation && step.kind !== StepKind.Mixed) {
    return false;
  }
  
  // Must have skills or actions to execute
  const hasSkills = step.skills && step.skills.length > 0;
  const hasActions = step.actions && step.actions.length > 0;
  
  return (hasSkills || hasActions) ?? false;
}

